import { DomainError } from './errors';

/**
 * Money is always an integer amount of the currency's minor unit (spec §2, ADR-0010).
 * JS numbers are exact for integers up to 2^53 - 1, which covers any realistic restaurant
 * amount even for zero-decimal currencies such as IDR. Every operation re-checks safety.
 */
export interface Money {
  readonly amountMinor: number;
  readonly currency: CurrencyCode;
}

/** ISO 4217 code, upper-case, three letters. */
export type CurrencyCode = string & { readonly __brand?: 'CurrencyCode' };

/**
 * Minor-unit exponent per currency. IDR is technically exponent 2 in ISO 4217 but is
 * operated as a zero-decimal currency in practice; we store IDR in whole rupiah.
 * Payment adapters must convert explicitly if a provider expects a different exponent.
 */
const CURRENCY_EXPONENT: Record<string, number> = {
  IDR: 0,
  JPY: 0,
  KRW: 0,
  USD: 2,
  EUR: 2,
  SGD: 2,
  MYR: 2,
  AUD: 2,
};

/** Currencies the platform can price in (for selects and validation). */
export const SUPPORTED_CURRENCIES: readonly CurrencyCode[] = Object.keys(CURRENCY_EXPONENT);

export function currencyExponent(currency: CurrencyCode): number {
  const exp = CURRENCY_EXPONENT[currency];
  if (exp === undefined) {
    throw new DomainError('VALIDATION_FAILED', `Unsupported currency ${currency}`, { currency });
  }
  return exp;
}

export function isSupportedCurrency(currency: string): currency is CurrencyCode {
  return Object.hasOwn(CURRENCY_EXPONENT, currency);
}

function assertSafe(amountMinor: number): number {
  if (!Number.isSafeInteger(amountMinor)) {
    throw new DomainError('MONEY_OVERFLOW', 'Money amount must be a safe integer of minor units', {
      amountMinor,
    });
  }
  // Normalise -0 so equality and serialisation are predictable.
  return amountMinor === 0 ? 0 : amountMinor;
}

export function money(amountMinor: number, currency: CurrencyCode): Money {
  currencyExponent(currency);
  return { amountMinor: assertSafe(amountMinor), currency };
}

export function zero(currency: CurrencyCode): Money {
  return money(0, currency);
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new DomainError('CURRENCY_MISMATCH', 'Cannot combine different currencies', {
      left: a.currency,
      right: b.currency,
    });
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function sum(items: readonly Money[], currency: CurrencyCode): Money {
  return items.reduce((acc, m) => add(acc, m), zero(currency));
}

export function negate(a: Money): Money {
  return money(-a.amountMinor, a.currency);
}

export function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  return a.amountMinor === b.amountMinor ? 0 : a.amountMinor < b.amountMinor ? -1 : 1;
}

export type RoundingMode = 'half_up' | 'half_even' | 'down' | 'up';

/**
 * Integer division with an explicit rounding mode. Uses BigInt internally so that
 * intermediate products (amount * numerator) can never lose precision.
 */
export function divideRounded(numerator: bigint, denominator: bigint, mode: RoundingMode): bigint {
  if (denominator === 0n) throw new DomainError('INVARIANT_VIOLATION', 'Division by zero');
  if (denominator < 0n) {
    numerator = -numerator;
    denominator = -denominator;
  }
  const q = numerator / denominator; // truncates toward zero
  const r = numerator % denominator;
  if (r === 0n) return q;
  const negative = numerator < 0n;
  const twiceR = (r < 0n ? -r : r) * 2n;
  const awayFromZero = negative ? q - 1n : q + 1n;
  switch (mode) {
    case 'down':
      return negative ? q - 1n : q; // floor
    case 'up':
      return negative ? q : q + 1n; // ceil
    case 'half_up':
      return twiceR >= denominator ? awayFromZero : q;
    case 'half_even':
      if (twiceR > denominator) return awayFromZero;
      if (twiceR < denominator) return q;
      return q % 2n === 0n ? q : awayFromZero;
  }
}

/**
 * Multiply money by a rational rate, e.g. a tax rate expressed in basis points
 * (`rateNumerator = 1100, rateDenominator = 10000` for 11%).
 */
export function multiplyByRate(
  a: Money,
  rateNumerator: number,
  rateDenominator: number,
  mode: RoundingMode = 'half_up',
): Money {
  if (!Number.isSafeInteger(rateNumerator) || !Number.isSafeInteger(rateDenominator)) {
    throw new DomainError('VALIDATION_FAILED', 'Rate parts must be integers');
  }
  const result = divideRounded(
    BigInt(a.amountMinor) * BigInt(rateNumerator),
    BigInt(rateDenominator),
    mode,
  );
  return money(Number(result), a.currency);
}

/**
 * Split an amount into `parts` shares that sum exactly to the original. The rounding
 * residue goes to the first shares (largest-remainder), so the backend decides it
 * deterministically (spec §8.2).
 */
export function allocateEvenly(a: Money, parts: number): Money[] {
  if (!Number.isSafeInteger(parts) || parts <= 0) {
    throw new DomainError('VALIDATION_FAILED', 'parts must be a positive integer');
  }
  const total = BigInt(a.amountMinor);
  const n = BigInt(parts);
  const base = total / n;
  let residue = total - base * n;
  const step = residue < 0n ? -1n : 1n;
  const out: Money[] = [];
  for (let i = 0; i < parts; i++) {
    let share = base;
    if (residue !== 0n) {
      share += step;
      residue -= step;
    }
    out.push(money(Number(share), a.currency));
  }
  return out;
}

/**
 * Format for display using the branch locale. Converts minor units to a major-unit
 * string without floating point by building the decimal string manually.
 */
export function formatMoney(a: Money, locale: string): string {
  const exp = currencyExponent(a.currency);
  const negative = a.amountMinor < 0;
  const digits = Math.abs(a.amountMinor)
    .toString()
    .padStart(exp + 1, '0');
  const major = exp === 0 ? digits : `${digits.slice(0, -exp)}.${digits.slice(-exp)}`;
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: a.currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  });
  // Intl.NumberFormat accepts decimal strings exactly (ES2023 "string as exact decimal").
  return formatter.format(`${negative ? '-' : ''}${major}` as unknown as number);
}
